/**
 * Title, description and JSON-LD of a public profile page (/u/:id).
 *
 * Provider profiles are indexed (Search Console: 9 valid ProfilePage items) but used to say
 * nothing: the title was the bare name, the description the site-wide fallback "Ein
 * Online-Marktplatz." and the schema a Person with only a name. What a provider offers and where
 * is already on the page — their listings — so the head says it too.
 *
 * All keys are repo-only on purpose: 'ProfilePage.schemaTitle' is overridden in the Console.
 */
import { groupKeyForCategoryId } from '../../components/CategoryShortcuts/categoryGroups';
import { LOUNGE_MATCHER } from '../../util/airportLoungeConfig';
import { siteRootUrl, websiteRef } from '../../util/siteStructuredData';

// Display order of the offer types in a title: the order of the city page pills, lounges last.
const PROFILE_TYPE_ORDER = ['desk', 'meeting', 'office', 'lounge'];
const LOUNGE_LISTING_TYPE = /^lounge$/i;
const MAX_DESCRIPTION_LENGTH = 155;
const MAX_SCHEMA_BIO_LENGTH = 1000;
// Sentinel for ICU `select`: an empty string is not a valid selector value.
const NONE = 'none';

/**
 * Which kinds of space a provider offers, from the categories of their listings.
 *
 * @param {Array<Object>} listings listing entities
 * @returns {Array<'desk'|'meeting'|'office'|'lounge'>} in display order, without duplicates
 */
export const offerTypeKeys = (listings = []) => {
  const keys = new Set();
  listings.forEach(listing => {
    const { categoryLevel1, listingType } = listing?.attributes?.publicData || {};
    if (
      LOUNGE_MATCHER.test(`${categoryLevel1 || ''}`) ||
      LOUNGE_LISTING_TYPE.test(`${listingType || ''}`)
    ) {
      keys.add('lounge');
      return;
    }
    const group = groupKeyForCategoryId(categoryLevel1);
    if (group) {
      keys.add(group);
    }
  });
  return PROFILE_TYPE_ORDER.filter(key => keys.has(key));
};

/**
 * The locality of a geocoded address: the part after the postcode.
 * "Bahnhofstrasse 10, 8001 Zürich, Schweiz" → "Zürich".
 *
 * @param {string} address publicData.location.address
 * @returns {string|null}
 */
export const localityFromAddress = address => {
  const parts = `${address || ''}`
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const withPostcode = parts.find(part => /^\d{4,5}\s+\S/.test(part));
  const locality = withPostcode ? withPostcode.replace(/^\d{4,5}\s+/, '').trim() : '';
  return locality || null;
};

/**
 * The city most of a provider's listings are in (first seen wins a tie).
 *
 * @param {Array<Object>} listings listing entities
 * @returns {string|null}
 */
export const mainCity = (listings = []) => {
  const counts = new Map();
  listings.forEach(listing => {
    const city = localityFromAddress(listing?.attributes?.publicData?.location?.address);
    if (city) {
      counts.set(city, (counts.get(city) || 0) + 1);
    }
  });
  let best = null;
  counts.forEach((count, city) => {
    if (best == null || count > counts.get(best)) {
      best = city;
    }
  });
  return best;
};

/**
 * @param {string} text user-written text
 * @returns {string} on one line, whitespace collapsed
 */
export const plainText = text => `${text || ''}`.replace(/\s+/g, ' ').trim();

/**
 * Shortens at a word boundary and marks the cut.
 *
 * @param {string} text plain text
 * @param {number} maxLength
 * @returns {string}
 */
export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) {
    return text;
  }
  const cut = text.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength / 2 ? cut.slice(0, lastSpace) : cut).replace(
    /[\s,.;:–-]+$/,
    ''
  )}…`;
};

const formatTypes = (intl, typeKeys) => {
  const labels = typeKeys.map(key => intl.formatMessage({ id: `ProfilePage.seoType.${key}` }));
  if (labels.length === 0) {
    return intl.formatMessage({ id: 'ProfilePage.seoType.other' });
  }
  return typeof intl.formatList === 'function'
    ? intl.formatList(labels, { type: 'conjunction' })
    : labels.join(', ');
};

const avatarUrl = user => {
  const variants = user?.profileImage?.attributes?.variants || {};
  return (
    variants['square-small2x']?.url ||
    variants['square-small']?.url ||
    Object.values(variants).find(v => v?.url)?.url ||
    null
  );
};

const isoDate = value => {
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
};

/**
 * @param {Object} params
 * @param {Object} params.intl react-intl
 * @param {Object} params.config marketplace config
 * @param {Object} params.user the profile user entity
 * @param {string} params.userId UUID of the profile (from the route)
 * @param {Array<Object>} params.listings the user's published listings
 * @returns {{ title: string, description: string, schema: Object, hasListings: boolean,
 *   hasBio: boolean }}
 */
export const profilePageSeo = ({ intl, config, user, userId, listings = [] }) => {
  const { displayName = '', bio } = user?.attributes?.profile || {};
  const marketplaceName = config.marketplaceName;
  const name = displayName || '';
  const hasListings = listings.length > 0;
  const bioText = plainText(bio);
  const hasBio = bioText.length > 0;

  const typeKeys = offerTypeKeys(listings);
  const city = mainCity(listings);
  const types = formatTypes(intl, typeKeys);
  const values = { name, marketplaceName, types, city: city || NONE, count: listings.length };

  const title = hasListings
    ? intl.formatMessage({ id: 'ProfilePage.seoTitle' }, values)
    : intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, { name, marketplaceName });

  const description = hasListings
    ? intl.formatMessage({ id: 'ProfilePage.seoDescription' }, values)
    : hasBio
    ? truncateText(bioText, MAX_DESCRIPTION_LENGTH)
    : intl.formatMessage({ id: 'ProfilePage.seoDescriptionNoListings' }, values);

  const uuid = user?.id?.uuid || userId;
  const profileUrl = `${siteRootUrl(config.marketplaceRootURL)}/u/${uuid}`;
  const dateCreated = isoDate(user?.attributes?.createdAt);
  const image = avatarUrl(user);

  // A provider with listings is a business (Organization), everyone else a Person — the
  // ProfilePage rich result accepts both as mainEntity.
  const mainEntity = {
    '@type': hasListings ? 'Organization' : 'Person',
    '@id': `${profileUrl}#${hasListings ? 'provider' : 'person'}`,
    name,
    identifier: uuid,
    ...(hasBio ? { description: truncateText(bioText, MAX_SCHEMA_BIO_LENGTH) } : {}),
    ...(image ? { image } : {}),
    agentInteractionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/WriteAction',
      userInteractionCount: listings.length,
    },
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${profileUrl}#profile`,
    url: profileUrl,
    name: title,
    ...(dateCreated ? { dateCreated } : {}),
    isPartOf: websiteRef(config.marketplaceRootURL),
    mainEntity,
  };

  return { title, description, schema, hasListings, hasBio };
};
